import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useInstall } from '../lib/pwa';
import { Avatar } from './Avatar';
import type { Community, Me } from '../types/api';

/**
 * Corner user chip that opens a dropdown with My page / My settings / My
 * desk (manage) / Admin (staff only) / Log out. Newspaper-metaphor label
 * for the manage screen — an editor's desk is where posts, groups and
 * community moderation happen.
 */
export function TopbarMe() {
    const api = useApi();
    const { logout, kc } = useAuth();
    const { canPrompt, standalone, ios, install } = useInstall();
    const [me, setMe] = useState<Me | null>(null);
    const [communities, setCommunities] = useState<Community[]>([]);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        api.get<Me>('/api/me').then(setMe).catch(() => { /* pre-login etc */ });
        // Load communities so the dropdown can show a "Your communities"
        // section jumping straight to that community's newsroom. Cheap:
        // small list, only when authed.
        api.get<{ communities: Community[] }>('/api/communities')
            .then(r => setCommunities(r.communities ?? []))
            .catch(() => { /* pre-login etc */ });
    }, [kc?.token]);

    // Close on outside click + Escape.
    useEffect(() => {
        function onDoc(e: MouseEvent) {
            if (!open) return;
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const preferAnon = typeof window !== 'undefined' && window.localStorage.getItem('onhere_prefer_anon') === '1';
    const claims = (kc?.tokenParsed ?? {}) as Record<string, string>;
    const cornerName = preferAnon && me?.anon_display_identity
        ? me.anon_display_identity
        : me?.display_name
        || [claims.given_name, claims.family_name].filter(Boolean).join(' ')
        || claims.preferred_username || claims.email || 'You';
    const isStaff = (me as Me & { is_staff?: boolean })?.is_staff;
    // Communities the user founded — moderators-only surfaces (newsroom,
    // dashboard) live behind that check server-side. Server-side "mine"
    // filter doesn't exist yet, so filter client-side by founder ID.
    const myCommunities = me?.id
        ? communities.filter(c => c.created_by_user_id === me.id)
        : [];

    return (
        <div className="me" ref={wrapRef} style={{ position: 'relative' }}>
            <button
                type="button"
                className="me-link"
                onClick={() => setOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                style={{
                    background: 'none', border: 'none', padding: 0, font: 'inherit',
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
            >
                <Avatar src={me?.avatar_url} name={cornerName} seed={me?.id} anonymous={preferAnon} size={24} />
                {cornerName}
                <span style={{ fontSize: 10, color: '#888' }}>▾</span>
            </button>

            {open && (
                <div role="menu" style={menuStyle}>
                    <Item to="/profile"  label="My page"     hint="How others see you"          onGo={() => setOpen(false)} />
                    <Item to="/settings" label="My settings" hint="Avatar, home, notifications" onGo={() => setOpen(false)} />
                    <Item to="/manage"   label="My desk"     hint="Groups, communities, editor" onGo={() => setOpen(false)} />
                    {myCommunities.length > 0 && (
                        <>
                            <div style={dividerStyle} />
                            <div style={sectionLabelStyle}>Your communities</div>
                            {myCommunities.map(c => (
                                <Item
                                    key={c.id}
                                    to={'/communities/' + c.slug + '/newsroom'}
                                    label={c.name}
                                    hint="Newsroom · feeds, queue, activity"
                                    onGo={() => setOpen(false)}
                                />
                            ))}
                        </>
                    )}
                    {isStaff && (
                        <>
                            <div style={dividerStyle} />
                            <div style={sectionLabelStyle}>★ Staff</div>
                            <Item to="/admin"           label="Admin overview"   hint="Signals, activity, editor stats" onGo={() => setOpen(false)} />
                            <Item to="/admin/ai-agents" label="AI Agents"        hint="Editors, reporters, curators"    onGo={() => setOpen(false)} />
                            <Item to="/admin/feeds"     label="Feeds"            hint="RSS / Nitter / web scan sources" onGo={() => setOpen(false)} />
                            <Item to="/admin/map"       label="Map"              hint="Communities, places, heatmap"    onGo={() => setOpen(false)} />
                            <Item to="/admin/claims"    label="Claims"           hint="Group claim & appeal queue"      onGo={() => setOpen(false)} />
                            <Item to="/admin/analytics" label="Analytics"        hint="DAU, visits, headline numbers"   onGo={() => setOpen(false)} />
                        </>
                    )}
                    {!standalone && (
                        <>
                            <div style={dividerStyle} />
                            <button
                                type="button"
                                onClick={async () => {
                                    setOpen(false);
                                    if (canPrompt) {
                                        await install();
                                    } else if (ios) {
                                        alert('To install on iPhone:\n\n1. Tap the Share icon (square with an arrow) at the bottom of Safari\n2. Scroll and tap "Add to Home Screen"\n3. Tap Add\n\nNote: this only works in Safari, not Chrome or Firefox on iPhone.');
                                    } else {
                                        alert('To install on Android:\n\n1. Tap the ⋮ menu in Chrome (top-right)\n2. Tap "Install app" or "Add to Home screen"\n\nIf you don\'t see it, keep using the site for a minute — Chrome enables install once it decides the app is worth keeping.');
                                    }
                                }}
                                style={btnStyle}
                            >
                                <span style={labelStyle}>Install app</span>
                                <span style={hintStyle}>
                                    {canPrompt
                                        ? 'Full-screen paper on your home screen'
                                        : ios
                                        ? 'Safari → Share → Add to Home Screen'
                                        : 'Chrome menu → Install app'}
                                </span>
                            </button>
                        </>
                    )}
                    <div style={dividerStyle} />
                    <button type="button" onClick={() => { setOpen(false); logout(); }} style={btnStyle}>
                        <span style={labelStyle}>Log out</span>
                    </button>
                </div>
            )}
        </div>
    );
}

function Item({ to, label, hint, tone, onGo }: {
    to: string; label: string; hint?: string; tone?: 'danger'; onGo: () => void;
}) {
    return (
        <Link to={to} onClick={onGo} style={{ ...btnStyle, textDecoration: 'none' }}>
            <span style={{ ...labelStyle, color: tone === 'danger' ? 'var(--accent)' : 'var(--ink)' }}>{label}</span>
            {hint && <span style={hintStyle}>{hint}</span>}
        </Link>
    );
}

const menuStyle: React.CSSProperties = {
    position: 'absolute', right: 0, top: '100%', marginTop: 6,
    minWidth: 240,
    background: '#fff',
    border: '1px solid #111',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    zIndex: 1000,
    padding: '6px 0',
};
const btnStyle: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '8px 14px',
    background: 'none', border: 'none', cursor: 'pointer',
    font: 'inherit', color: 'inherit',
};
const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: "'Newsreader', Georgia, serif",
    fontSize: 15, fontWeight: 500, color: 'var(--ink)',
};
const hintStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 11, color: '#888', marginTop: 1,
};
const dividerStyle: React.CSSProperties = {
    borderTop: '1px dotted #cfc9b7', margin: '4px 0',
};
const sectionLabelStyle: React.CSSProperties = {
    padding: '4px 14px', fontSize: 10, color: '#888',
    textTransform: 'uppercase', letterSpacing: 2,
    fontFamily: '-apple-system,sans-serif',
};
